'use client';

import { forwardRef, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { SliderRootContext } from './context/slider-root.context';
import { sliderActiveTrackStyles } from './styles/slider-active-track.styles';
import type { SliderActiveTrackPropsInterface } from './types/slider-active-track.types';

/**
 * The Slider's active portion of the background bar
 *
 * @since 10.0.0
 */
export const SliderActiveTrack = forwardRef<HTMLDivElement, SliderActiveTrackPropsInterface>(
  (props, forwardedRef) => {
    const { className, css: cssProp, style, ...rest } = props;

    const { max, min, value, reverse } = useContext(SliderRootContext);

    const width = useMemo(() => {
      if (!value) return '0%';
      if (Array.isArray(value)) {
        const start = (100 * (value[0] - min)) / (max - min);
        const end = (100 * (value[1] - min)) / (max - min);
        return `${end - start}%`;
      }

      return `${100 * ((value - min) / (max - min))}%`;
    }, [max, min, value]);

    const offset = useMemo(() => {
      if (!value || !Array.isArray(value)) return '0%';
      return `${(100 * (value[0] - min)) / (max - min)}%`;
    }, [max, min, value]);

    const positionProperty = useMemo(() => {
      return reverse ? 'insetInlineEnd' : 'insetInlineStart';
    }, [reverse]);

    const styles = css(sliderActiveTrackStyles, cssProp);

    return (
      <div
        className={cx('c11n', 'c11n_slider__active-track', styles, className)}
        data-c11n-component="Slider.ActiveTrack"
        data-c11n-version={__C11N_VERSION__}
        ref={forwardedRef}
        style={{
          width,
          [positionProperty]: offset,
          ...style,
        }}
        {...rest}
      />
    );
  },
);

SliderActiveTrack.displayName = 'Slider.ActiveTrack';
